import { useEffect, useRef, useState } from 'react';
import { getDocumentUploadReceipt, uploadFilesToFolder } from '@/api/document.api';
import { extensionValidator } from '@/utils/extensionValidator';
import { validateDocumentFilename } from '@/utils/filenameValidator';
import { getDocumentUploadErrorKind, DocumentUploadErrorKind } from './uploadError';

export type UploadItem = { key: string; name: string; size: number; folderId: number | null; file?: File;
  status: 'ready' | 'uploading' | 'success' | 'failed' | 'unknown'; error?: DocumentUploadErrorKind };
const uuid = () => {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (v) => v.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0,8)}-${hex.slice(8,12)}-${hex.slice(12,16)}-${hex.slice(16,20)}-${hex.slice(20)}`;
};
const read = (owner: string): UploadItem[] => {
  try { return JSON.parse(sessionStorage.getItem(`document-uploads:${owner}`) || '[]').map((item: UploadItem) => ({
    ...item, file: undefined, status: item.status === 'uploading' ? 'unknown' : item.status === 'ready' ? 'failed' : item.status,
  })); } catch { return []; }
};

export const useUploadBatch = (owner: string) => {
  const [state, setState] = useState(() => ({ owner, items: read(owner) }));
  const [busy, setBusy] = useState(false);
  const [storageError, setStorageError] = useState(false);
  const holder = useRef(state);
  const lock = useRef(false);
  const generation = useRef(owner);
  generation.current = owner;
  useEffect(() => { holder.current = { owner, items: read(owner) }; setState(holder.current); }, [owner]);
  const items = state.owner === owner ? state.items : [];
  const change = (update: (items: UploadItem[]) => UploadItem[]) => {
    if (generation.current !== owner) return;
    const current = holder.current;
    if (current.owner !== owner) return false;
    const next = update(current.items);
    let persisted = true;
    try { sessionStorage.setItem(`document-uploads:${owner}`, JSON.stringify(next.map(({ file, ...item }) => item))); }
    catch { persisted = false; setStorageError(true); }
    holder.current = { owner, items: next }; setState(holder.current);
    return persisted;
  };
  const patch = (key: string, update: Partial<UploadItem>) => change((current) => current.map((item) => item.key === key ? { ...item, ...update } : item));
  const send = async (item: UploadItem) => {
    if (!item.file || generation.current !== owner) return;
    if (!extensionValidator([item.file]) || !validateDocumentFilename([item.file])) { patch(item.key, { status: 'failed', error: 'validation' }); return; }
    if (!patch(item.key, { status: 'uploading', error: undefined })) return;
    try {
      const response = await uploadFilesToFolder({ files: [item.file], folderId: item.folderId }, item.key);
      patch(item.key, response.data.success ? { status: 'success' } : { status: 'failed', error: 'general' });
    } catch (error: any) {
      const status = error?.response?.status;
      patch(item.key, { status: status && status >= 400 && status < 500 && status !== 408 ? 'failed' : 'unknown', error: getDocumentUploadErrorKind(error) });
    }
  };
  const start = async (files: File[], folderId: number | null) => {
    if (lock.current) return;
    lock.current = true; setBusy(true);
    try {
      const batch = files.map((file) => ({ key: uuid(), name: file.name, size: file.size, folderId, file, status: 'ready' as const }));
      if (!change((current) => [...current.filter((item) => item.status !== 'success'), ...batch])) return;
      for (const item of batch) await send(item);
    } finally { lock.current = false; setBusy(false); }
  };
  const check = async (item: UploadItem) => {
    if (lock.current) return;
    lock.current = true; setBusy(true);
    try {
      const response = await getDocumentUploadReceipt(item.key);
      patch(item.key, { status: response.data.data.state === 'completed' ? 'success' : 'failed', error: undefined });
    } catch { patch(item.key, { status: 'unknown' }); }
    finally { lock.current = false; setBusy(false); }
  };
  const retry = async (item: UploadItem, replacement?: File) => {
    if (lock.current || item.status !== 'failed') return;
    const file = replacement || item.file;
    if (!file || file.name !== item.name || file.size !== item.size) { patch(item.key, { error: 'validation' }); return; }
    lock.current = true; setBusy(true);
    patch(item.key, { file });
    try { await send({ ...item, file }); } finally { lock.current = false; setBusy(false); }
  };
  return { items, busy, storageError, start, check, retry };
};
